import React, { useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import {
  Alert,
  Loader,
  SimpleGrid,
  Table,
  Text,
  Title,
  Center,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconFileInvoice,
  IconCoin,
} from "@tabler/icons";
import { useAuthUser } from "react-auth-kit";

import { useServerApi } from "../hooks/userServerApi";
import { useCellRender } from "../hooks/useCellRender";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { resolveEntityPath } from "../utils/resolveEntityPath";
import CardStat from "./cardStat";

const ClientOrderSummary = ({ form }) => {
  const clientId = _.get(form.values, "_id");
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const orderLinkPath = resolveEntityPath("order", auth()?.userRole);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const openOrder = (e, order) => {
    e.preventDefault();
    if (!order?._id || !orderLinkPath) return;
    if (tabNav.enabled) {
      tabNav.openDetail({
        pathname: orderLinkPath,
        id: order._id,
        mode: "edit",
        label: order.code,
      });
      return;
    }
    window.open(`${orderLinkPath}?id=${order._id}&mode=edit`, "_blank");
  };

  const fetchOrderSummary = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const result = await api.Client.getOrderSummary(clientId);
      if (result?.success) {
        setSummary(result.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderSummary();
  }, [clientId]);

  if (!clientId) {
    return (
      <Text size="xs" color="dimmed">
        Save the client record to view order summary.
      </Text>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!summary) {
    return (
      <Text size="xs" color="dimmed">
        No order summary data available.
      </Text>
    );
  }

  const highValueCount = summary.highValueLumpSumOrders?.length ?? 0;
  const statCards = [
    {
      icon: IconFileInvoice,
      color: "teal",
      label: "NB (12 Months)",
      msg: String(summary.nbCount12Months ?? 0),
    },
    {
      icon: IconCoin,
      color: "blue",
      label: "Total Premium",
      msg: cellRender.Currency(summary.totalPremiumHkd, "HKD"),
    },
    {
      icon: highValueCount > 0 ? IconAlertTriangle : IconCircleCheck,
      color: highValueCount > 0 ? "orange" : "green",
      label: "High-Value Lump Sum",
      msg: String(highValueCount),
    },
  ];

  return (
    <>
      <Title order={5} mb={4}>
        EDD Order Summary
      </Title>
      <Text size="xs" color="dimmed" mb="lg">
        Order metrics for enhanced due diligence requirement checking.
      </Text>

      <SimpleGrid cols={3} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mb="xl">
        {statCards.map((stat, index) => (
          <CardStat key={index} stat={stat} />
        ))}
      </SimpleGrid>

      <Title order={6} mb="sm">
        Lump Sum Premium &gt; HKD 3M
      </Title>
      {highValueCount > 0 && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="orange"
          mb="sm"
          title="EDD flag"
        >
          {highValueCount} order(s) with lump sum premium exceeding HKD 3M.
        </Alert>
      )}
      {highValueCount === 0 ? (
        <Text size="sm" color="dimmed" mb="xl">
          No orders with lump sum premium exceeding HKD 3M.
        </Text>
      ) : (
        <Table fontSize="sm" striped highlightOnHover mb="xl">
          <thead>
            <tr>
              <th>Order</th>
              <th>Product Type</th>
              <th>Signed Date</th>
              <th>Premium (HKD)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {summary.highValueLumpSumOrders.map((order) => (
              <tr key={order._id}>
                <td>
                  <Text
                    component="a"
                    style={{ textDecoration: "none", cursor: "pointer" }}
                    onClick={(e) => openOrder(e, order)}
                  >
                    {order.code}
                  </Text>
                  {order.policyNumber && (
                    <Text color="dimmed" size="xs">
                      {order.policyNumber}
                    </Text>
                  )}
                </td>
                <td>{order.productTypeName || "-"}</td>
                <td>
                  {order.signedDate
                    ? moment(order.signedDate).format("YYYY-MM-DD")
                    : "-"}
                </td>
                <td>
                  {cellRender.Currency(order.yearPremium, "HKD")}
                </td>
                <td>{cellRender.Order.status(order.status)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Title order={6} mb="sm">
        Sum Insured by Product Type
      </Title>
      {_.isEmpty(summary.sumInsuredByProductType) ? (
        <Text size="sm" color="dimmed">
          No sum insured data available.
        </Text>
      ) : (
        <Table fontSize="sm" striped highlightOnHover>
          <thead>
            <tr>
              <th>Product Type</th>
              <th>Orders</th>
              <th>Total Sum Insured</th>
              <th>Total Premium (HKD)</th>
            </tr>
          </thead>
          <tbody>
            {summary.sumInsuredByProductType.map((row) => (
              <tr key={row.productTypeName}>
                <td>{row.productTypeName}</td>
                <td>{row.orderCount}</td>
                <td>{cellRender.Currency(row.totalSumInsured, "HKD")}</td>
                <td>{cellRender.Currency(row.totalPremiumHkd, "HKD")}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default ClientOrderSummary;
